import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Delete,
  Description,
  InsertDriveFile,
} from '@mui/icons-material';
import { candidatesApi } from '../services/api';

interface FileAnalysis {
  tempFilename: string;
  originalFilename: string;
  size: number;
  documentType: 'resume' | 'coverLetter';
  parsed: { firstName: string | null; lastName: string | null; confidence: number };
  match: {
    candidateId: number;
    candidateName: string;
    confidence: number;
    matchSource?: 'filename' | 'content';
    hasExisting: boolean;
  } | null;
}

interface CandidateOption {
  id: number;
  name: string;
  email: string;
}

interface FileAssignment {
  tempFilename: string;
  originalFilename: string;
  candidateId: number | null;
  documentType: 'resume' | 'coverLetter';
  status: 'matched' | 'manual' | 'skipped';
  matchSource?: 'filename' | 'content';
}

interface BulkDocumentUploadProps {
  onComplete?: () => void;
}

const BulkDocumentUpload: React.FC<BulkDocumentUploadProps> = ({ onComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [files, setFiles] = useState<FileAssignment[]>([]);
  const [candidates, setCandidates] = useState<CandidateOption[]>([]);
  const [stats, setStats] = useState<{ total: number; matched: number; unmatched: number } | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await analyzeFiles(droppedFiles);
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    if (selectedFiles.length > 0) {
      await analyzeFiles(selectedFiles);
    }
    // Reset input
    e.target.value = '';
  }, []);

  const analyzeFiles = async (fileList: File[]) => {
    setIsAnalyzing(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      fileList.forEach(file => {
        formData.append('documents', file);
      });

      const response = await candidatesApi.bulkAnalyze(formData);
      const data = response.data;

      setStats({
        total: data.totalFiles,
        matched: data.matched,
        unmatched: data.unmatched,
      });

      setCandidates(data.candidates);

      setFiles(data.files.map((f: FileAnalysis) => ({
        tempFilename: f.tempFilename,
        originalFilename: f.originalFilename,
        candidateId: f.match?.candidateId || null,
        documentType: f.documentType,
        status: f.match ? 'matched' : 'manual',
        matchSource: f.match?.matchSource,
      })));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze files');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCandidateChange = (tempFilename: string, candidateId: number | null) => {
    setFiles(prev => prev.map(f =>
      f.tempFilename === tempFilename
        ? { ...f, candidateId, status: candidateId ? 'manual' : 'skipped' }
        : f
    ));
  };

  const handleDocumentTypeChange = (tempFilename: string, documentType: 'resume' | 'coverLetter') => {
    setFiles(prev => prev.map(f =>
      f.tempFilename === tempFilename
        ? { ...f, documentType }
        : f
    ));
  };

  const handleRemoveFile = (tempFilename: string) => {
    setFiles(prev => prev.filter(f => f.tempFilename !== tempFilename));
  };

  const handleConfirm = async () => {
    setIsUploading(true);
    setError(null);

    try {
      const assignments = files.map(f => ({
        tempFilename: f.tempFilename,
        candidateId: f.candidateId,
        documentType: f.documentType,
      }));

      const response = await candidatesApi.bulkConfirm(assignments);
      const results = response.data.results;

      setSuccess(
        `Successfully uploaded ${results.success.length} documents. ` +
        `${results.skipped.length} skipped. ` +
        (results.errors.length > 0 ? `${results.errors.length} errors.` : '')
      );

      // Clear the file list
      setFiles([]);
      setStats(null);

      if (onComplete) {
        onComplete();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload documents');
    } finally {
      setIsUploading(false);
    }
  };

  const assignedCount = files.filter(f => f.candidateId).length;
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      {/* Drop Zone */}
      {files.length === 0 && (
        <Paper
          sx={{
            p: 4,
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.300',
            bgcolor: isDragging ? 'action.hover' : 'background.paper',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('bulk-file-input')?.click()}
        >
          <input
            id="bulk-file-input"
            type="file"
            multiple
            accept=".pdf,.docx"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drop files here or click to select
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Supports PDF and DOCX files. Candidates matched by filename or document content.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Best results when filename includes name (e.g., John_Smith_Resume.pdf) or document contains candidate's name/email.
          </Typography>
        </Paper>
      )}

      {/* Loading State */}
      {isAnalyzing && (
        <Box textAlign="center" py={4}>
          <CircularProgress />
          <Typography variant="body1" mt={2}>
            Analyzing files and matching to candidates...
          </Typography>
        </Box>
      )}

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Results */}
      {files.length > 0 && !isAnalyzing && (
        <>
          {/* Stats Summary */}
          {stats && (
            <Box display="flex" gap={2} mb={2}>
              <Chip
                icon={<InsertDriveFile />}
                label={`${stats.total} files`}
                variant="outlined"
              />
              <Chip
                icon={<CheckCircle />}
                label={`${stats.matched} matched`}
                color="success"
                variant="outlined"
              />
              <Chip
                icon={<Warning />}
                label={`${stats.unmatched} need review`}
                color="warning"
                variant="outlined"
              />
            </Box>
          )}

          {/* File Table */}
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Assign to Candidate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.tempFilename}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Description fontSize="small" color="action" />
                        <Tooltip title={file.originalFilename}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {file.originalFilename}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={file.documentType}
                          onChange={(e) => handleDocumentTypeChange(file.tempFilename, e.target.value as 'resume' | 'coverLetter')}
                        >
                          <MenuItem value="resume">Resume</MenuItem>
                          <MenuItem value="coverLetter">Cover Letter</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        sx={{ minWidth: 280 }}
                        options={candidates}
                        getOptionLabel={(option) => option.name + (option.email ? ` (${option.email})` : '')}
                        value={candidates.find(c => c.id === file.candidateId) || null}
                        onChange={(_, newValue) => handleCandidateChange(file.tempFilename, newValue?.id || null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search candidates..."
                            size="small"
                          />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        clearOnBlur
                        blurOnSelect
                      />
                    </TableCell>
                    <TableCell>
                      {file.status === 'matched' && file.candidateId && (
                        <Tooltip title={file.matchSource === 'content' ? 'Matched by PDF content' : 'Matched by filename'}>
                          <Chip
                            size="small"
                            icon={<CheckCircle />}
                            label={file.matchSource === 'content' ? 'Content match' : 'Filename match'}
                            color="success"
                          />
                        </Tooltip>
                      )}
                      {file.status === 'manual' && file.candidateId && (
                        <Chip size="small" icon={<CheckCircle />} label="Assigned" color="info" />
                      )}
                      {!file.candidateId && (
                        <Chip size="small" icon={<Warning />} label="Will skip" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleRemoveFile(file.tempFilename)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {assignedCount} of {files.length} files will be uploaded
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFiles([]);
                  setStats(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={assignedCount === 0 || isUploading}
                startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
              >
                {isUploading ? 'Uploading...' : `Upload ${assignedCount} Documents`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BulkDocumentUpload;
